import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

/**
 * End-of-day check-in: what I did today, what I'm doing tomorrow. Opens
 * with "done today" pre-filled from the employee's own ticket activity
 * for the day (see _build_suggested_activity_text in tickets/views.py) --
 * purely a starting point, freely editable before submitting. Submitting
 * emails a copy to DAILY_REPORT_RECIPIENT_EMAIL and saves it to the
 * in-app archive (admin-only). Submitting again the same day updates the
 * existing report rather than creating a second one, so this same form
 * doubles as "amend today's report" if opened again later in the day.
 */
const DailyReportModal = ({ onClose, onSubmitted }) => {
  const [loading, setLoading] = useState(true);
  const [doneToday, setDoneToday] = useState('');
  const [planTomorrow, setPlanTomorrow] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [work, setWork] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getDailyReportToday()
      .then((data) => {
        setDoneToday(data.done_today || '');
        setPlanTomorrow(data.plan_tomorrow || '');
        setAlreadySubmitted(!!data.submitted);
        setWork(data.work || null);
      })
      .catch(() => toast.error('Could not load today\'s report.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (!doneToday.trim() || !planTomorrow.trim()) {
      toast.error('Both fields are required.');
      return;
    }
    try {
      setSubmitting(true);
      await api.submitDailyReport({ doneToday, planTomorrow });
      toast.success(alreadySubmitted ? 'Report updated.' : 'Report submitted.');
      onSubmitted?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not submit the report.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Daily report</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>
          {alreadySubmitted
            ? 'You\'ve already submitted today\'s report -- submitting again updates it.'
            : "\"What I did today\" starts pre-filled from your own ticket activity -- edit it freely before submitting."}
        </p>

        {loading ? <p>Loading...</p> : (
          <>
            {work && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                <strong>Your working day</strong>
                <div>Started: {new Date(work.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {work.start_outside_office && <span style={{ color: '#ffcf7a', fontWeight: 700 }}> · outside office</span>}</div>
                <div style={{ color: '#aab7c4' }}>
                  {work.finished_at
                    ? `Finished: ${new Date(work.finished_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} (updates if you submit again)`
                    : 'Finish time is recorded when you submit this report. Your start/finish times and IP address are included in the report.'}
                </div>
              </div>
            )}
            <div className="section-title" style={{ marginTop: 0 }}>What I did today</div>
            <textarea
              className="form-textarea" style={{ minHeight: 140, marginBottom: '0.75rem' }}
              value={doneToday} onChange={(e) => setDoneToday(e.target.value)}
            />

            <div className="section-title">What I'm doing tomorrow</div>
            <textarea
              className="form-textarea" style={{ minHeight: 100 }}
              value={planTomorrow} onChange={(e) => setPlanTomorrow(e.target.value)}
              placeholder="e.g. Follow up with the vendor about the paper feed."
            />
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={submitting || loading}>
            {submitting ? 'Submitting...' : alreadySubmitted ? 'Update report' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyReportModal;
